/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import Box from '@mui/material/Box';
import type { Position, Source } from '@opengeoweb/shared';
import { ToolContainerDraggable } from '@opengeoweb/shared';
import { LayerType, WMDefaultStyleName } from '@opengeoweb/webmap';
import type { LayerFoundation, LayerProps } from '@opengeoweb/webmap';
import { LayerInfoLegend } from './LayerInfoLegend';
import { LayerInfoList } from './LayerInfoList';
import { LayerInfoText } from './LayerInfoText';
import {
  getDimensionsList,
  getLayerBbox,
  getLayerStyles,
} from './LayerInfoUtils';
import { useWebmapReactTranslation } from '../../utils/i18n';

export interface LayerInfoDialogProps {
  layer: LayerProps;
  serviceName: string;
  serviceUrl?: string;
  onClose: () => void;
  isOpen: boolean;
  onMouseDown?: () => void;
  order?: number;
  source?: Source;
  dialogHeight?: number;
  startPosition?: Position;
  selectedStyle?: string;
  layerType?: LayerType;
  layerOpacity?: number;
  stacLayer?: Pick<LayerFoundation, 'service' | 'id' | 'style'>;
  showLayerLegend: boolean;
  onToggleLayerLegendVisibility?: (showLegend: boolean) => void;
  edrParameterMetadata?: {
    id?: string;
    label?: string;
    description?: string;
    observedProperty?: { label?: string };
    unit?: { label?: string; symbol?: { value?: string } };
  };
}
export const LayerInfoDialog: React.FC<LayerInfoDialogProps> = ({
  onClose,
  layer,
  serviceName,
  serviceUrl,
  isOpen,
  onMouseDown = (): void => {},
  order = 0,
  source = 'app',
  dialogHeight,
  startPosition = { top: 150, left: 900 },
  selectedStyle,
  layerType,
  layerOpacity,
  stacLayer,
  showLayerLegend,
  onToggleLayerLegendVisibility,
  edrParameterMetadata,
}: LayerInfoDialogProps) => {
  const styles = getLayerStyles(layer);
  const dimensions = getDimensionsList(layer.dimensions!);
  const { t } = useWebmapReactTranslation();
  const bbox = getLayerBbox(t, layer);

  const legendURL =
    layer.styles?.find((styleItem) => styleItem.name === selectedStyle)
      ?.legendURL ||
    // show the layer's default style, or the first style, if none selected
    layer.styles?.find((styleItem) => styleItem.name === WMDefaultStyleName)
      ?.legendURL ||
    layer.styles?.[0]?.legendURL ||
    undefined;

  return (
    <ToolContainerDraggable
      isOpen={isOpen}
      onClose={onClose}
      onMouseDown={onMouseDown}
      order={order}
      source={source}
      startPosition={startPosition}
      startSize={{ width: 288, height: dialogHeight || 650 }}
      title={t('webmap-react-layer-info')}
    >
      <Box sx={{ padding: 2 }}>
        <LayerInfoText label={t('webmap-react-title')} value={layer.title!} />
        <LayerInfoText label={t('webmap-react-name')} value={layer.name!} />
        {!!edrParameterMetadata && (
          <>
            <LayerInfoText
              label="EDR parameter"
              value={edrParameterMetadata.id || layer.name!}
            />
            <LayerInfoText
              label="Observed property"
              value={edrParameterMetadata.observedProperty?.label || '-'}
            />
            <LayerInfoText
              label="Unit"
              value={
                edrParameterMetadata.unit?.symbol?.value ||
                edrParameterMetadata.unit?.label ||
                '-'
              }
            />
          </>
        )}
        <LayerInfoText label={t('webmap-react-service')} value={serviceName} />
        <LayerInfoText
          label={t('webmap-react-service-url')}
          value={serviceUrl || '-'}
        />

        <LayerInfoText
          label={t('webmap-react-abstract')}
          value={layer.abstract || '-'}
        />
        <LayerInfoList label={t('webmap-react-dimensions')} list={dimensions} />
        <LayerInfoText label={t('webmap-react-styles')} value={styles} />
        <LayerInfoText label={t('webmap-react-bounding-box')} value={bbox} />
        <LayerInfoText
          label={t('webmap-react-groups')}
          value={layer.path?.join('/') || '-'}
        />
        <LayerInfoText
          label={t('webmap-react-keywords')}
          value={layer.keywords?.join(', ') || '-'}
        />
        <LayerInfoLegend
          layerOpacity={layerOpacity}
          layerType={layerType}
          legendURL={legendURL}
          onToggleLayerLegendVisibility={onToggleLayerLegendVisibility}
          showLayerLegend={showLayerLegend}
          stacLayer={stacLayer}
          title={
            layerType === LayerType.stacGeoTIFFLayer
              ? layer.name || layer.title!
              : layer.title!
          }
        />
      </Box>
    </ToolContainerDraggable>
  );
};
